import { Box, Flex } from '@televet/kibble-ui/build/chakra';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooterButtons,
  ModalHeader,
} from '@televet/kibble-ui/build/components/Modal';
import { Text } from '@televet/kibble-ui/build/components/Text';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import isEmpty from 'lodash-es/isEmpty';
import {
  LoyaltyProgramRewardFragment,
  LoyaltyRewardAvailability,
  useCreateLoyaltyProgramRewardMutation,
  useUpdateLoyaltyProgramRewardMutation,
} from 'shared/types/graphql';
import { STATUSES, useCustomToast } from 'shared/hooks/useCustomToast';
import { BaseEmoji, Picker } from 'emoji-mart';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { Switch } from '@televet/kibble-ui/build/components/Switch';
import { Textarea } from '@televet/kibble-ui/build/components/Textarea';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Icon } from '@televet/kibble-ui/build/components/Icon';

interface RewardModalProps {
  programId: string;
  selectedReward?: LoyaltyProgramRewardFragment;
  isOpen: boolean;
  onClose: () => void;
  triggerRefetch: () => void;
}

const emptyReward: Omit<LoyaltyProgramRewardFragment, 'id'> = {
  title: '',
  description: '',
  pointCost: 0,
  availability: LoyaltyRewardAvailability.Published,
};

enum ValidationField {
  Title = 'title',
  Description = 'description',
  PointCost = 'pointCost',
}

type RewardErrors = {
  [key in ValidationField]?: boolean;
};

const TITLE_MAX_CHAR_COUNT = 50;
const DESCRIPTION_MAX_CHAR_COUNT = 200;

export const RewardModal = ({
  programId,
  selectedReward,
  isOpen,
  onClose,
  triggerRefetch,
}: RewardModalProps): JSX.Element => {
  const { toastMessagePop } = useCustomToast();
  const [reward, setReward] = useState<Omit<LoyaltyProgramRewardFragment, 'id'>>(selectedReward || emptyReward);
  const [errors, setErrors] = useState<RewardErrors>({});
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const emojiPicker = useRef<HTMLDivElement | null>(null);
  const emojiButton = useRef<HTMLButtonElement>(null);
  const descriptionTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const isEditing = !!selectedReward;

  const [updateReward, { loading: updatingReward }] = useUpdateLoyaltyProgramRewardMutation();
  const [createReward, { loading: creatingReward }] = useCreateLoyaltyProgramRewardMutation();

  const handleSave = async (): Promise<void> => {
    const validations = {
      [ValidationField.Title]: validateByField(ValidationField.Title, reward.title),
      [ValidationField.Description]: validateByField(ValidationField.Description, reward.description),
      [ValidationField.PointCost]: validateByField(ValidationField.PointCost, reward.pointCost),
    };
    setErrors({ ...validations });
    if (Object.values(validations).every((error) => !error)) {
      try {
        isEditing
          ? await updateReward({
              variables: {
                where: {
                  id: selectedReward?.id,
                },
                data: {
                  title: reward.title,
                  description: reward.description,
                  pointCost: reward.pointCost,
                  availability: reward.availability,
                },
              },
            })
          : await createReward({
              variables: {
                data: {
                  program: {
                    connect: {
                      id: programId,
                    },
                  },
                  ...reward,
                },
              },
            });
        toastMessagePop();
      } catch {
        toastMessagePop(
          `Whoops! There was a problem ${
            isEditing ? 'editing' : 'creating'
          } the reward, please refresh the page and try again.`,
          STATUSES.error,
        );
      } finally {
        triggerRefetch();
        onClose();
      }
    }
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function handleClickOutside(event: any): void {
      if (emojiButton.current?.contains(event.target)) {
        return;
      } else if (!emojiPicker.current?.contains(event.target)) {
        setIsEmojiPickerOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [emojiPicker, setIsEmojiPickerOpen]);

  const handleEmojiSelect = useCallback(
    (emoji: BaseEmoji): void => {
      if (!descriptionTextareaRef.current) return;

      const selectionStart = descriptionTextareaRef.current.selectionStart;
      const selectionEnd = descriptionTextareaRef.current.selectionEnd;
      setReward((currentReward) => {
        return {
          ...currentReward,
          description: `${currentReward.description.slice(0, selectionStart)}${
            emoji.native
          }${currentReward.description.slice(selectionEnd)}`,
        };
      });
      const emojiLength = emoji.native.length;
      const newStart = selectionStart + emojiLength;
      const newEnd = selectionEnd + emojiLength;

      descriptionTextareaRef.current.setSelectionRange(newStart, newEnd);

      setIsEmojiPickerOpen(!isEmojiPickerOpen);
      descriptionTextareaRef.current?.focus();
      setTimeout(() => {
        descriptionTextareaRef.current?.setSelectionRange(newStart, newEnd);
      }, 0);
    },
    [isEmojiPickerOpen],
  );

  const isInvalid = useMemo(
    () => (isEmpty(errors) ? (isEditing ? false : true) : Object.values(errors).some((error) => !!error)),
    [errors, isEditing],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered={false} size="md" allowEscape={false}>
      <ModalCloseButton />
      <ModalHeader>{isEditing ? 'Edit' : 'New'} Reward</ModalHeader>
      <ModalBody>
        <Box mb={6}>
          <Text as="p" fontWeight="bold">
            Reward Name <Text variant="danger">*</Text>
          </Text>
          <Text as="p" size="sm" variant="subtle" mb={2}>
            This is the name of the rewards clients will see in their pet portal.
          </Text>
          <TextInput
            name="title"
            helperText={`${reward?.title.length}/${TITLE_MAX_CHAR_COUNT}`}
            value={reward.title}
            errorText="Field can't be empty or above 100 characters"
            onChange={(e): void => {
              setReward({ ...reward, title: e.target.value });
              setErrors({ ...errors, [ValidationField.Title]: validateByField(ValidationField.Title, e.target.value) });
            }}
            isInvalid={errors[ValidationField.Title]}
          />
        </Box>
        <Box mb={6} position="relative">
          <Textarea
            name="description"
            helperText={`${reward?.description.length}/${DESCRIPTION_MAX_CHAR_COUNT}`}
            label="Description"
            labelStyle={{ fontWeight: 'bold' }}
            value={reward.description}
            onChange={(e): void => {
              setReward({ ...reward, description: e.target.value });
              setErrors({
                ...errors,
                [ValidationField.Description]: validateByField(ValidationField.Description, e.target.value),
              });
            }}
            errorText="Field can't be above 100 characters"
            isInvalid={errors[ValidationField.Description]}
            ref={descriptionTextareaRef}
          />
          <Box position="absolute" right={0} mt={errors[ValidationField.Description] ? 2 : -3}>
            <Button
              variant="secondarySubtle"
              onClick={(): void => setIsEmojiPickerOpen(!isEmojiPickerOpen)}
              size="sm"
              ref={emojiButton}
              iconSpacing="2px"
              padding="6px 8px"
              leftIcon={<Icon name="smileyFace" />}
              rightIcon={<Icon name="chevronDown" size="md" />}
            />
            {isEmojiPickerOpen && (
              <Box ref={emojiPicker}>
                <Picker
                  autoFocus
                  native
                  title="Pick your emoji…"
                  emoji="point_up"
                  style={{ position: 'absolute', zIndex: 10, marginTop: '8px' }}
                  onSelect={handleEmojiSelect}
                />
              </Box>
            )}
          </Box>
        </Box>
        <Box mb={6}>
          <Flex alignItems="center">
            <Box alignItems="center" mr={6}>
              <Text as="p" fontWeight="bold">
                Points Value <Text variant="danger">*</Text>
              </Text>
              <Text as="p" size="sm" variant="subtle">
                This represents how many points it takes to earn this reward.
              </Text>
            </Box>
            <TextInput
              name="pointCost"
              type="number"
              isRequired
              value={reward.pointCost >= 1 ? reward.pointCost : ''}
              onChange={(e): void => {
                setReward({ ...reward, pointCost: Number(e.target.value) });
                setErrors({
                  ...errors,
                  [ValidationField.PointCost]: validateByField(ValidationField.PointCost, Number(e.target.value)),
                });
              }}
              width="80px"
              isInvalid={errors[ValidationField.PointCost]}
            />
          </Flex>
          {errors[ValidationField.PointCost] && (
            <Text as="p" size="xs" variant="danger" textAlign="right" mt={-2} maxW="40%" ml="auto">
              Field can&apos;t be empty and must be an integer
            </Text>
          )}
        </Box>
        <Box>
          <Switch
            label="Active?"
            isFieldInline
            labelStyle={{ fontWeight: 'bold' }}
            isChecked={reward.availability === LoyaltyRewardAvailability.Published}
            onChange={(): void =>
              setReward({
                ...reward,
                availability:
                  reward.availability === LoyaltyRewardAvailability.Published
                    ? LoyaltyRewardAvailability.Unpublished
                    : LoyaltyRewardAvailability.Published,
              })
            }
          />
        </Box>
      </ModalBody>
      <ModalFooterButtons
        onSubmit={handleSave}
        isDisabled={isInvalid || updatingReward || creatingReward}
        isLoading={updatingReward || creatingReward}
      />
    </Modal>
  );
};

const validateByField = (field: ValidationField, value: string | number): boolean => {
  switch (field) {
    case ValidationField.Title:
      return typeof value === 'string' && (value === '' || value.length > TITLE_MAX_CHAR_COUNT);
    case ValidationField.Description:
      return typeof value === 'string' && value.length > DESCRIPTION_MAX_CHAR_COUNT;
    case ValidationField.PointCost:
      return typeof value === 'number' && (value < 1 || value % 1 !== 0);
    default:
      return false;
  }
};
